import { t } from 'i18next';
import { useEffect, useState } from 'react';

import { AxiosError } from 'axios';
import InfoButton from '../../../../../components/InfoButton';
import Input from '../../../../../components/Input';
import Modal from '../../../../../components/Modal';
import {
  backendErrorNotification,
  frontendNotification,
} from '../../../../../components/Notification';
import SingleSelect from '../../../../../components/SingleSelect';
import HttpStatus from '../../../../../enums/httpStatus';
import { sellersApi } from '../../../../../services/requests';
import { processReportReq } from '../../../../../services/requests/reports';
import { ReportGet, ReportParameterGet } from '../../../../../types';
import { getExtensionForTheReports } from '../../../../../utils/getSelectOptions';
import {
  buildSafeReportFilenameFromDescription,
  downloadBlobFile
} from '../../../../../utils/fileDownload';
import * as s from './styles';

type Props = {
  selectedItem: ReportGet;
  closeModal: () => void;
};

type ParamToPost = {
  label: string;
  value: any;
};

const ModalProcessReportJSX = ({ selectedItem, closeModal }: Props) => {
  const [waitingReq, setWaitingReq] = useState(false);

  const [extension, setExtension] = useState(getExtensionForTheReports()[0]);
  const [parameters, setParameters] = useState<ParamToPost[]>([]);

  const reqProcessReport = async () => {
    if (!selectedItem.active) {
      frontendNotification({
        message: t('inactive'),
        type: 'warning',
      });
      return;
    }

    setWaitingReq(true);

    try {
      const copyParameters = [...parameters];
      const supervisedSellersIndex = copyParameters.findIndex(
        (p) => p.label === 'supervisedSellers',
      );

      if (supervisedSellersIndex !== -1) {
        sellersApi.query = '?quantityPerPage=1000000';
        const responseSellers = await sellersApi.index();
        if (responseSellers.status === HttpStatus.OK) {
          const sellers = responseSellers.data.data;
          const sellerCodes = sellers.map((s: any) => `'${s.CodigoVendedor}'`).join(',');
          copyParameters[supervisedSellersIndex].value = sellerCodes;
        }
      }

      const res = await processReportReq(selectedItem.id, extension.value, copyParameters);

      if (res.status === HttpStatus.OK) {
        let fileType = 'application/pdf';
        switch (extension.value) {
          case 'xls':
            fileType = 'application/vnd.ms-excel';
            break;
          case 'xlsx':
            fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
            break;
          case 'csv':
            fileType = 'text/csv';
            break;
          case 'rtf':
            fileType = 'application/rtf';
            break;
          case 'docx':
            fileType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
            break;
          case 'odt':
            fileType = 'application/vnd.oasis.opendocument.text';
            break;
          case 'ods':
            fileType = 'application/vnd.oasis.opendocument.spreadsheet';
            break;
          case 'pptx':
            fileType = 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
            break;
          case 'html':
            fileType = 'text/html';
            break;
          case 'xml':
            fileType = 'application/xml';
            break;
          default:
            fileType = 'application/pdf';
            break;
        }

        const file = new Blob([res.data], { type: fileType });

        const fileName = buildSafeReportFilenameFromDescription(
          selectedItem.description,
          extension.value,
          60,
        );
        downloadBlobFile(file, fileName);

        frontendNotification({
          message: t('successfullyProcessed'),
          type: 'success',
        });

        setWaitingReq(false);
        closeModal();
      } else {
        throw res;
      }
    } catch (err: any) {
      // frontendNotification({
      //   message: t('errorTryingToProcessReport'),
      //   type: 'error',
      // });
      backendErrorNotification(err as AxiosError<any, any>);
    }

    setWaitingReq(false);
  };

  const handleInputParameter = (parameter: string, value: any) => {
    const copyParameters: ParamToPost[] = [...parameters];
    const index = copyParameters.findIndex((object) => object.label === parameter);
    copyParameters[index].value = value;
    setParameters(copyParameters);
  };

  const handleInputValue = (parameter: string) => {
    const copyParameters: ParamToPost[] = [...parameters];
    const index = copyParameters.findIndex((object) => object.label === parameter);
    if (copyParameters[index]) {
      return copyParameters[index].value;
    }

    return '';
  };

  const handleInputType = (param: ReportParameterGet) => (
    <input
      type="text"
      className="input"
      value={handleInputValue(param.parameter)}
      onChange={(e: any) => handleInputParameter(param.parameter, e.target.value)}
    />
  );

  useEffect(() => {
    const newParameters: any = [];
    selectedItem.parameters.forEach((item) => {
      newParameters.push({
        label: item.parameter,
        value: '',
      });
    });

    setParameters(newParameters);
  }, []);

  return (
    <Modal
      title={`${t('processReport')}`}
      action={() => reqProcessReport()}
      showModal
      closeModal={closeModal}
      cancelButton={closeModal}
      actionNameBtn={t('execute')}
      isLoading={waitingReq}
      bodyStyle={selectedItem.parameters.length >= 5 ? { overflow: 'scroll' } : {}}
    >
      <s.Container>
        <s.Row>
          <s.Item>
            <p>{t('description') as string}: </p>
            <Input value={selectedItem.description} onChange={() => null} />
          </s.Item>
          <s.Item id="extension">
            <p>{t('extension') as string}: </p>
            <SingleSelect
              value={extension}
              options={getExtensionForTheReports()}
              onChange={(e: any) => setExtension(e)}
              disabled={waitingReq}
              placeholder={t('select')}
            />
          </s.Item>
        </s.Row>

        <s.Row>
          {selectedItem.parameters.length >= 1 &&
            selectedItem.parameters.map((item) => {
              if (item.parameter === 'supervisedSellers') return null;

              return (
                <s.Item key={item.parameter}>
                  <s.ParameterInfo>
                    <p>{item.parameter}</p>
                    <InfoButton msg={item.description} />
                  </s.ParameterInfo>

                  {handleInputType(item)}
                </s.Item>
              );
            })}
        </s.Row>
      </s.Container>
    </Modal>
  );
};

export default ModalProcessReportJSX;
